import React, { useState, useEffect, useRef } from 'react'
import { CheckIcon, AlertCircleIcon, InfoIcon } from 'lucide-react'
import { supabase } from '../lib/supabase'

interface ProposalLineItem {
  id: string
  service_item_id?: string
  description: string
  quantity: number
  is_variable: boolean
  unit_type: string
  unit_price: number
  total_price: number
  sort_order: number
  // Calculated fields
  allocated_quantity?: number
  remaining_quantity?: number
}

interface SelectedLineItem {
  proposal_line_item_id: string
  service_item_id?: string
  description: string
  quantity: number
  is_variable: boolean
  unit_type: string
  unit_price: number
  total_price: number
  estimated_quantity?: number // For variable items
  max_quantity: number
}

interface JobLineItemSelectorProps {
  proposalId: string
  onSelectionChange: (items: SelectedLineItem[], total: number) => void
  initialSelection?: SelectedLineItem[]
}

export function JobLineItemSelector({
  proposalId,
  onSelectionChange,
  initialSelection = [],
}: JobLineItemSelectorProps) {
  const [lineItems, setLineItems] = useState<ProposalLineItem[]>([])
  const [selectedItems, setSelectedItems] = useState<Map<string, SelectedLineItem>>(new Map())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (proposalId) {
      fetchLineItemsWithAllocations()
    }
  }, [proposalId])

  // Initialize with initial selection only once on mount
  const hasInitialized = useRef(false)
  useEffect(() => {
    if (!hasInitialized.current && initialSelection.length > 0) {
      const map = new Map<string, SelectedLineItem>()
      initialSelection.forEach((item) => {
        map.set(item.proposal_line_item_id, item)
      })
      setSelectedItems(map)
      hasInitialized.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run once on mount

  useEffect(() => {
    // Calculate total and notify parent
    const items = Array.from(selectedItems.values())
    const total = items.reduce((sum, item) => {
      if (item.is_variable) {
        return sum + (item.estimated_quantity || 0) * item.unit_price
      }
      return sum + item.quantity * item.unit_price
    }, 0)
    onSelectionChange(items, total)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItems]) // Only depend on selectedItems, not onSelectionChange

  const fetchLineItemsWithAllocations = async () => {
    try {
      setLoading(true)
      setError(null)

      // Fetch proposal line items
      const { data: items, error: itemsError } = await supabase
        .from('proposal_line_items')
        .select('*')
        .eq('proposal_id', proposalId)
        .order('sort_order')

      if (itemsError) throw itemsError

      // Fetch existing allocations for these line items
      const itemIds = items?.map((i) => i.id) || []

      let allocations: { proposal_line_item_id: string; allocated_quantity: number }[] = []
      if (itemIds.length > 0) {
        const { data: allocData, error: allocError } = await supabase
          .from('proposal_line_item_allocations')
          .select('proposal_line_item_id, allocated_quantity')
          .in('proposal_line_item_id', itemIds)

        if (allocError) {
          console.warn('Could not fetch allocations:', allocError)
        } else {
          allocations = allocData || []
        }
      }

      // Calculate allocated and remaining quantities
      const itemsWithAllocations = (items || []).map((item) => {
        const allocated = allocations
          .filter((a) => a.proposal_line_item_id === item.id)
          .reduce((sum, a) => sum + (a.allocated_quantity || 0), 0)

        return {
          ...item,
          allocated_quantity: allocated,
          remaining_quantity: Math.max(0, (item.quantity || 1) - allocated),
        }
      })

      setLineItems(itemsWithAllocations)
    } catch (err) {
      console.error('Error fetching line items:', err)
      setError('Failed to load line items')
    } finally {
      setLoading(false)
    }
  }

  const toggleItem = (item: ProposalLineItem) => {
    const newSelected = new Map(selectedItems)

    if (newSelected.has(item.id)) {
      newSelected.delete(item.id)
    } else {
      // Add item with default quantity (remaining or 1 for variable)
      const quantity = item.is_variable ? 0 : (item.remaining_quantity || item.quantity || 1)
      newSelected.set(item.id, {
        proposal_line_item_id: item.id,
        service_item_id: item.service_item_id,
        description: item.description,
        quantity: item.is_variable ? 0 : quantity,
        is_variable: item.is_variable,
        unit_type: item.unit_type,
        unit_price: item.unit_price,
        total_price: item.is_variable ? 0 : quantity * item.unit_price,
        estimated_quantity: item.is_variable ? 1 : undefined,
        max_quantity: item.remaining_quantity || item.quantity || 1,
      })
    }

    setSelectedItems(newSelected)
  }

  const updateQuantity = (itemId: string, quantity: number) => {
    const newSelected = new Map(selectedItems)
    const item = newSelected.get(itemId)

    if (item) {
      const maxQty = lineItems.find((li) => li.id === itemId)?.remaining_quantity || item.max_quantity
      const clampedQty = Math.min(Math.max(0, quantity), maxQty)

      newSelected.set(itemId, {
        ...item,
        quantity: clampedQty,
        total_price: clampedQty * item.unit_price,
      })
      setSelectedItems(newSelected)
    }
  }

  const updateEstimatedQuantity = (itemId: string, estimated: number) => {
    const newSelected = new Map(selectedItems)
    const item = newSelected.get(itemId)

    if (item && item.is_variable) {
      newSelected.set(itemId, {
        ...item,
        estimated_quantity: Math.max(0, estimated),
      })
      setSelectedItems(newSelected)
    }
  }

  const selectAll = () => {
    const newSelected = new Map<string, SelectedLineItem>()
    lineItems.forEach((item) => {
      if ((item.remaining_quantity || 0) > 0 || item.is_variable) {
        const quantity = item.is_variable ? 0 : (item.remaining_quantity || item.quantity || 1)
        newSelected.set(item.id, {
          proposal_line_item_id: item.id,
          service_item_id: item.service_item_id,
          description: item.description,
          quantity: item.is_variable ? 0 : quantity,
          is_variable: item.is_variable,
          unit_type: item.unit_type,
          unit_price: item.unit_price,
          total_price: item.is_variable ? 0 : quantity * item.unit_price,
          estimated_quantity: item.is_variable ? 1 : undefined,
          max_quantity: item.remaining_quantity || item.quantity || 1,
        })
      }
    })
    setSelectedItems(newSelected)
  }

  const clearAll = () => {
    setSelectedItems(new Map())
  }

  const formatUnitType = (unitType: string) => {
    return unitType.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
  }

  if (loading) {
    return (
      <div className="p-4 text-center text-gray-500">
        Loading line items...
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4 text-center text-red-600">
        <AlertCircleIcon className="w-5 h-5 inline mr-2" />
        {error}
      </div>
    )
  }

  if (lineItems.length === 0) {
    return (
      <div className="p-4 text-center text-gray-500">
        No line items found in this proposal.
      </div>
    )
  }

  const selectedTotal = Array.from(selectedItems.values()).reduce((sum, item) => {
    if (item.is_variable) {
      return sum + (item.estimated_quantity || 0) * item.unit_price
    }
    return sum + item.quantity * item.unit_price
  }, 0)

  return (
    <div className="space-y-4">
      {/* Header with select all/clear */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-700">
          Select Line Items for this Job
        </h4>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={selectAll}
            className="text-xs text-blue-600 hover:text-blue-800"
          >
            Select All Available
          </button>
          <span className="text-gray-300">|</span>
          <button
            type="button"
            onClick={clearAll}
            className="text-xs text-gray-600 hover:text-gray-800"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Info banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <InfoIcon className="w-4 h-4 text-blue-600 mt-0.5" />
          <div className="text-xs text-blue-800">
            <p>Select which proposal line items to include in this job. For items with quantity, you can specify how much to allocate to this job (useful when splitting a proposal across multiple jobs).</p>
            <p className="mt-1"><strong>Variable items</strong> (hourly, per-visit, etc.) will be billed based on actual usage reported during job completion.</p>
          </div>
        </div>
      </div>

      {/* Line items list */}
      <div className="border border-gray-200 rounded-lg divide-y divide-gray-200">
        {lineItems.map((item) => {
          const isSelected = selectedItems.has(item.id)
          const selectedItem = selectedItems.get(item.id)
          const isFullyAllocated = !item.is_variable && (item.remaining_quantity || 0) <= 0

          return (
            <div
              key={item.id}
              className={`p-4 ${isFullyAllocated ? 'bg-gray-50 opacity-60' : ''}`}
            >
              <div className="flex items-start gap-4">
                {/* Checkbox */}
                <div className="pt-1">
                  <button
                    type="button"
                    onClick={() => !isFullyAllocated && toggleItem(item)}
                    disabled={isFullyAllocated}
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                      isSelected
                        ? 'bg-blue-600 border-blue-600 text-white'
                        : isFullyAllocated
                        ? 'border-gray-300 bg-gray-100 cursor-not-allowed'
                        : 'border-gray-300 hover:border-blue-400'
                    }`}
                  >
                    {isSelected && <CheckIcon className="w-3 h-3" />}
                  </button>
                </div>

                {/* Item details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {item.description}
                      </p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-gray-500">
                          ${item.unit_price.toFixed(2)} / {formatUnitType(item.unit_type)}
                        </span>
                        {item.is_variable && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                            Variable
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Allocation status */}
                    <div className="text-right">
                      {!item.is_variable && (
                        <div className="text-xs text-gray-500">
                          <span>Original: {item.quantity}</span>
                          {(item.allocated_quantity || 0) > 0 && (
                            <span className="ml-2 text-blue-600">
                              Allocated: {item.allocated_quantity}
                            </span>
                          )}
                          <span className="ml-2 font-medium text-gray-700">
                            Available: {item.remaining_quantity}
                          </span>
                        </div>
                      )}
                      {isFullyAllocated && (
                        <span className="text-xs text-gray-500">
                          Fully allocated to other jobs
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Quantity input when selected */}
                  {isSelected && selectedItem && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-4">
                        {item.is_variable ? (
                          // Variable item - estimated quantity
                          <div className="flex-1">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Estimated Quantity (for budgeting)
                            </label>
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                min="0"
                                step="0.5"
                                value={selectedItem.estimated_quantity || 0}
                                onChange={(e) =>
                                  updateEstimatedQuantity(item.id, parseFloat(e.target.value) || 0)
                                }
                                className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                              />
                              <span className="text-sm text-gray-500">
                                {formatUnitType(item.unit_type)}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              Actual will be recorded during job completion
                            </p>
                          </div>
                        ) : (
                          // Fixed item - quantity allocation
                          <div className="flex-1">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Quantity for this Job
                            </label>
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                min="0"
                                max={item.remaining_quantity || item.quantity}
                                step="1"
                                value={selectedItem.quantity}
                                onChange={(e) =>
                                  updateQuantity(item.id, parseFloat(e.target.value) || 0)
                                }
                                className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                              />
                              <span className="text-sm text-gray-500">
                                of {item.remaining_quantity} available
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Line total */}
                        <div className="text-right">
                          <span className="text-xs text-gray-500">Line Total:</span>
                          <p className="text-sm font-medium text-gray-900">
                            {item.is_variable ? (
                              <span className="text-orange-600">
                                ~${((selectedItem.estimated_quantity || 0) * item.unit_price).toFixed(2)}
                              </span>
                            ) : (
                              `$${(selectedItem.quantity * item.unit_price).toFixed(2)}`
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Summary */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-sm text-gray-600">
              {selectedItems.size} item{selectedItems.size !== 1 ? 's' : ''} selected
            </span>
          </div>
          <div className="text-right">
            <span className="text-sm text-gray-600">Estimated Total:</span>
            <p className="text-xl font-bold text-gray-900">
              ${selectedTotal.toFixed(2)}
            </p>
            {Array.from(selectedItems.values()).some((i) => i.is_variable) && (
              <p className="text-xs text-orange-600">
                * Includes variable items (actual may differ)
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default JobLineItemSelector

